import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Code, Check, ArrowLeft, CreditCard, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../AuthContext';
import api from '../api';

const PaymentPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchPackages();
  }, [user, navigate]);

  const fetchPackages = async () => {
    try {
      const response = await api.get('/payment/packages');
      setPackages(response.data);
    } catch (error) {
      toast.error('Failed to load payment packages');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
  };

  const handleProceedToPayment = async () => {
    if (!selectedPackage) {
      toast.error('Please select a package');
      return;
    }

    setProcessing(true);
    try {
      const originUrl = window.location.origin;
      const response = await api.post('/payment/create-checkout', {
        package_id: selectedPackage.id,
        origin_url: originUrl
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setProcessing(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/student/dashboard')} data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
            <div className="flex items-center space-x-2">
              <Code className="w-6 h-6 text-violet-600" />
              <span className="text-lg font-bold text-slate-900">CodeITT</span>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 py-12 max-w-7xl">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-slate-900 mb-4">Choose Your Learning Plan</h1>
          <p className="text-lg text-slate-600">Select the package that fits your learning goals. All plans include:</p>
          <div className="flex flex-wrap justify-center gap-4 mt-4">
            <div className="flex items-center text-slate-700">
              <Check className="w-5 h-5 text-emerald-600 mr-2" />
              <span>1-on-1 Live Sessions</span>
            </div>
            <div className="flex items-center text-slate-700">
              <Check className="w-5 h-5 text-emerald-600 mr-2" />
              <span>Expert Instructors</span>
            </div>
            <div className="flex items-center text-slate-700">
              <Check className="w-5 h-5 text-emerald-600 mr-2" />
              <span>Flexible Scheduling</span>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`relative rounded-2xl border-2 p-8 cursor-pointer transition-all duration-300 ${
                selectedPackage?.id === pkg.id
                  ? 'border-violet-600 bg-violet-50 shadow-xl scale-105'
                  : 'border-slate-200 bg-white hover:border-violet-300 hover:shadow-lg'
              }`}
              onClick={() => handleSelectPackage(pkg)}
              data-testid={`package-${pkg.id}`}
            >
              {pkg.classes >= 96 && (
                <div className="absolute -top-3 right-8 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                  Popular
                </div>
              )}
              
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{pkg.name}</h3>
                <div className="flex items-baseline justify-center mb-2">
                  <span className="text-5xl font-bold text-violet-600">${pkg.price_monthly}</span>
                  <span className="text-slate-600 ml-2">/month</span>
                </div>
                <p className="text-slate-600 text-sm">{pkg.description}</p>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center text-slate-700">
                  <Check className="w-5 h-5 text-emerald-600 mr-3 flex-shrink-0" />
                  <span><strong>{pkg.classes} Classes</strong> (1 hour each)</span>
                </div>
                <div className="flex items-center text-slate-700">
                  <Check className="w-5 h-5 text-emerald-600 mr-3 flex-shrink-0" />
                  <span><strong>{pkg.hours} Hours</strong> of learning</span>
                </div>
                <div className="flex items-center text-slate-700">
                  <Calendar className="w-5 h-5 text-violet-600 mr-3 flex-shrink-0" />
                  <span>Monthly auto-debit</span>
                </div>
                <div className="flex items-center text-slate-700">
                  <Check className="w-5 h-5 text-emerald-600 mr-3 flex-shrink-0" />
                  <span>Priority support</span>
                </div>
                <div className="flex items-center text-slate-700">
                  <Check className="w-5 h-5 text-emerald-600 mr-3 flex-shrink-0" />
                  <span>Course certificates</span>
                </div>
              </div>

              <div className="pt-6 border-t border-slate-200">
                <div className="text-center text-sm text-slate-600">
                  <span className="font-medium">${(pkg.price_monthly / pkg.classes).toFixed(2)}</span> per class
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Selected Package Summary */}
        {selectedPackage && (
          <div className="bg-white rounded-2xl border-2 border-violet-200 p-8 mb-8 shadow-lg" data-testid="selected-package-summary">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Selected Plan Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <p className="text-slate-600 mb-2">Package</p>
                <p className="text-xl font-semibold text-slate-900">{selectedPackage.name}</p>
              </div>
              <div>
                <p className="text-slate-600 mb-2">Monthly Payment</p>
                <p className="text-xl font-semibold text-violet-600">${selectedPackage.price_monthly}/month</p>
              </div>
              <div>
                <p className="text-slate-600 mb-2">Total Classes</p>
                <p className="text-xl font-semibold text-slate-900">{selectedPackage.classes} classes</p>
              </div>
              <div>
                <p className="text-slate-600 mb-2">Total Hours</p>
                <p className="text-xl font-semibold text-slate-900">{selectedPackage.hours} hours</p>
              </div>
            </div>
            
            <div className="bg-violet-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-slate-700">
                <strong>Note:</strong> Your subscription will automatically renew each month. You can cancel anytime from your account settings.
              </p>
            </div>

            <Button
              onClick={handleProceedToPayment}
              disabled={processing}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white h-14 text-lg rounded-full font-medium shadow-lg"
              data-testid="proceed-payment-btn"
            >
              {processing ? (
                'Processing...'
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Proceed to Secure Payment
                </>
              )}
            </Button>
          </div>
        )}

        {/* Trust Indicators */}
        <div className="text-center mt-12">
          <p className="text-slate-600 mb-4">Secure payment powered by Stripe</p>
          <div className="flex justify-center items-center gap-6 text-slate-400">
            <span>🔒 SSL Encrypted</span>
            <span>💳 Secure Checkout</span>
            <span>✅ Money-back Guarantee</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
